import { useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Combobox } from '@/components/Combobox/Combobox'
import { priceCalculatorLoadingAtom } from '@/features/priceCalculator/PurchaseForm/priceCalculatorAtoms'
import { PriceIntentAnimal, usePriceIntentAvailableBreedsQuery } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import type {
  Breed,
  PetCatBreedsField as InputFieldPetCatBreeds,
  PetDogBreedsField as InputFieldPetDogBreeds,
} from '@/services/PriceCalculator/Field.types'
import { MIXED_BREED_OPTION_ID, UNKNOWN_BREED_ID } from '@/services/PriceCalculator/Field.types'
import { yStack } from 'ui/src/patterns/stack'
import { MixedBreedPicker } from './MixedBreedPicker/MixedBreedPicker'
import { useTranslateFieldLabel } from './useTranslateFieldLabel'

type Props = {
  field: InputFieldPetCatBreeds | InputFieldPetDogBreeds
}

export const PetBreedsField = ({ field }: Props) => {
  const animal = field.type === 'pet-dog-breeds' ? PriceIntentAnimal.Dog : PriceIntentAnimal.Cat
  const loading = useAtomValue(priceCalculatorLoadingAtom)
  const translateLabel = useTranslateFieldLabel()
  const [showMixedPicker, setShowMixedPicker] = useState(() => isMixedBreedSelection(field.defaultValue ?? []))
  const { t } = useTranslation('purchase-form')

  const { breeds, pureBreeds, defaultSelectedBreeds, comboboxAvailableBreeds, comboboxDefaultSelectedBreed } =
    usePetBreedFieldState(animal, field.defaultValue)

  const handleComboboxChange = (breed: Breed | null) => {
    setShowMixedPicker(breed?.id === MIXED_BREED_OPTION_ID)
  }

  return (
    <div className={yStack({ gap: 'xxs' })}>
      <Combobox
        // Re-mount Combobox when 'breeds' becomes available,
        // otherwise it might happen the case where 'defaultSelectedItem'
        // doesn't get taken into account due the absence of 'breeds'
        key={breeds.length}
        items={comboboxAvailableBreeds}
        defaultSelectedItem={comboboxDefaultSelectedBreed}
        displayValue={(item) => item.displayName}
        getFormValue={breedToFormValue}
        label={translateLabel(field.label)}
        placeholder={t('FIELD_BREEDS_PLACEHOLDER')}
        onSelectedItemChange={handleComboboxChange}
        name={field.name}
        required={field.required}
        disabled={loading}
        noMatchesMessage={t('FIELD_BREEDS_NO_OPTIONS')}
      />

      {showMixedPicker && (
        <MixedBreedPicker
          breeds={pureBreeds}
          defaultSelectedBreeds={defaultSelectedBreeds}
          name={field.name}
          loading={loading}
        />
      )}
    </div>
  )
}

const usePetBreedFieldState = (animal: PriceIntentAnimal, preSelectedBreedIds: Array<string> = []) => {
  const { t } = useTranslation('purchase-form')
  const { data, error } = usePriceIntentAvailableBreedsQuery({
    variables: {
      animal,
    },
  })

  if (error) {
    throw new Error(`PetBreedsField: Could not get available breeds for ${animal.toLowerCase()}`)
  }

  const MIXED_BREED_OPTION = useMemo<Breed>(
    () => ({
      id: MIXED_BREED_OPTION_ID,
      displayName: t('LABEL_MIXED_BREED'),
    }),
    [t],
  )

  const breeds = useMemo(() => data?.priceIntentAvailableBreeds ?? [], [data?.priceIntentAvailableBreeds])

  // 'Vet inte' only makes sense as part of a mixed breed, so it's moved from the main list to the top of the picker
  const unknownBreed = useMemo(() => breeds.find((breed) => breed.id === UNKNOWN_BREED_ID), [breeds])

  const knownBreeds = useMemo(() => breeds.filter((breed) => breed.id !== UNKNOWN_BREED_ID), [breeds])

  const pureBreeds = useMemo(() => {
    const knownPureBreeds = knownBreeds.filter((breed) => !breed.isMixedBreed)
    return unknownBreed ? [unknownBreed, ...knownPureBreeds] : knownPureBreeds
  }, [knownBreeds, unknownBreed])

  return useMemo(() => {
    let comboboxAvailableBreeds: Array<Breed> = []
    let comboboxDefaultSelectedBreed: Breed | null = null
    let defaultSelectedBreeds: Array<Breed> = []
    if (breeds.length > 0) {
      defaultSelectedBreeds = preSelectedBreedIds.map((breedId) => {
        const matchedBreed = breeds.find((breed) => breed.id === breedId)
        if (!matchedBreed) {
          logger.warn(`PetBreedsField: couldn't found breed of id ${breedId}`)
          return { id: breedId, displayName: breedId }
        }
        return { id: matchedBreed.id, displayName: matchedBreed.displayName }
      })

      comboboxAvailableBreeds = [MIXED_BREED_OPTION, ...knownBreeds]

      if (isMixedBreedSelection(preSelectedBreedIds)) {
        comboboxDefaultSelectedBreed = MIXED_BREED_OPTION
      } else if (defaultSelectedBreeds.length === 1) {
        comboboxDefaultSelectedBreed = defaultSelectedBreeds[0]
      }
    }

    return {
      breeds,
      pureBreeds,
      comboboxAvailableBreeds,
      comboboxDefaultSelectedBreed,
      defaultSelectedBreeds,
    }
  }, [breeds, knownBreeds, pureBreeds, preSelectedBreedIds, MIXED_BREED_OPTION])
}

const breedToFormValue = (breed: Breed) => breed.id

const isMixedBreedSelection = (breedIds: Array<string>) => breedIds.length > 1 || breedIds.includes(UNKNOWN_BREED_ID)
